import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { pushButtonClickEventToDataLayer } from '../../common/analytics';
import { paths } from '../../common/constants/pathVariables.js';
import {
  fixedBroadbandMsg,
  mobileBroadbandMsg,
  mobileM2MMsg,
  orderNewSimCardMsg,
  phoneSubscriptionMsg,
  simCardOnlyMsg,
  t,
} from '../../common/i18n/index.js';
import { isMultiBiz } from '../../common/utils/accountUtils';
import { IconAndText } from '../../components/IconAndText/IconAndText.js';
import { SelectOneDialog } from '../../components/SelectOneDialog/SelectOneDialog.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useConfig } from '../../public/site/ConfigProvider.js';
import { DataLoader } from '../../public/site/siteUtils';
import { SelectCompanyDialog } from './SelectCompanyDialog.js';

export interface SelectNewSubscriptionDialogProps {
  onCloseDialog: () => void;
  hideVoice?: boolean;
  hideBroadband?: boolean;
  hideBlankSim?: boolean;
  showBlankSimLink?: boolean;
  mdmId?: string;
  clickElementParent?: string;
}

const getTabletRowLength = (itemCount: number): 1 | 2 => {
  if (itemCount >= 2) {
    return 2;
  }
  return 1;
};

const getLaptopRowLength = (itemCount: number): 1 | 2 | 3 | 4 => {
  if (itemCount > 3) {
    return 4;
  }
  if (itemCount === 3) {
    return 3;
  }
  if (itemCount === 2) {
    return 2;
  }
  return 1;
};

export const SelectNewSubscriptionDialog = ({
  onCloseDialog,
  hideVoice,
  hideBroadband,
  hideBlankSim,
  showBlankSimLink,
  mdmId,
  clickElementParent,
}: SelectNewSubscriptionDialogProps) => {
  const navigate = useNavigate();
  const { classicSiteUrl } = useConfig();
  const { authenticatedUser, setActiveAccount } = useAuth();
  const [selectCompanyDialogUrl, setSelectCompanyDialogUrl] = useState<string>();

  const onClickWithAnalytics = (path: string, action: (path: string) => void) => () => {
    if (clickElementParent) {
      pushButtonClickEventToDataLayer(clickElementParent, window.location.origin + path);
    }
    action(path);
  };

  const handleNavOrDialog = (path: string) =>
    isMultiBiz(authenticatedUser) ? setSelectCompanyDialogUrl(path) : navigate(path);

  const blankSim = [
    {
      element: (
        <IconAndText icon="order-sim" text={t.MXPB(simCardOnlyMsg)} id="select-new-subscription-dialog-blank-sim" />
      ),
      onClick: onClickWithAnalytics(paths.ORDER_NEW_SIM_CARD, handleNavOrDialog),
    },
  ];

  const broadband = [
    {
      element: (
        <IconAndText icon="fixedbb" text={t.QXQ7(fixedBroadbandMsg)} id="select-new-subscription-dialog-fixedbb" />
      ),
      onClick: onClickWithAnalytics(paths.ANONYMOUS_FIXED_BROADBAND_ORDER, handleNavOrDialog),
    },
    {
      element: (
        <IconAndText icon="mobilebb" text={t.EQKJ(mobileBroadbandMsg)} id="select-new-subscription-dialog-mobilebb" />
      ),
      link: authenticatedUser?.selfServiceInOec ? `${classicSiteUrl}/tilaa-liittyma-ja-laajakaista` : undefined,
      onClick: !authenticatedUser?.selfServiceInOec
        ? onClickWithAnalytics(`${paths.PS_BROADBAND_SUBSCRIPTION_NEW}${mdmId ? '?mdmId=' + mdmId : ''}`, navigate)
        : undefined,
    },
    {
      element: (
        <IconAndText icon="mobile-m2m" text={t.A7CA(mobileM2MMsg)} id="select-new-subscription-dialog-mobile-m2m" />
      ),
      link: authenticatedUser?.selfServiceInOec ? `${classicSiteUrl}/elisa-laitenetti-ya` : undefined,
      onClick: !authenticatedUser?.selfServiceInOec
        ? onClickWithAnalytics(`${paths.PS_BROADBAND_SUBSCRIPTION_NEW_M2M}${mdmId ? '?mdmId=' + mdmId : ''}`, navigate)
        : undefined,
    },
  ];

  const voice = [
    {
      element: <IconAndText icon="sim" text={t.R6AR(phoneSubscriptionMsg)} id="select-new-subscription-dialog-voice" />,
      link: authenticatedUser?.selfServiceInOec ? `${classicSiteUrl}/tilaa-liittyma-ja-laajakaista` : undefined,
      onClick: !authenticatedUser?.selfServiceInOec
        ? onClickWithAnalytics(`${paths.PS_MOBILE_SUBSCRIPTION_NEW}${mdmId ? '?mdmId=' + mdmId : ''}`, navigate)
        : undefined,
    },
  ];

  const options = [...(hideVoice ? [] : voice), ...(hideBroadband ? [] : broadband), ...(hideBlankSim ? [] : blankSim)];

  return (
    <>
      {selectCompanyDialogUrl ? (
        <SelectCompanyDialog
          onCloseDialog={onCloseDialog}
          onContinue={selectedMdmId => {
            setActiveAccount(selectedMdmId, false, `${selectCompanyDialogUrl}?mdmId=${selectedMdmId}`);
          }}
        />
      ) : (
        <SelectOneDialog
          header={t.XCEN('Select subscription type')}
          maxItemsAsideTablet={getTabletRowLength(options.length)}
          maxItemsAsideLaptop={getLaptopRowLength(options.length)}
          onCloseDialog={onCloseDialog}
          options={options}
        >
          <>
            {showBlankSimLink && (
              <p>
                <Link
                  to={paths.ORDER_NEW_SIM_CARD}
                  onClick={e => {
                    e.preventDefault();
                    onClickWithAnalytics(paths.ORDER_NEW_SIM_CARD, handleNavOrDialog)();
                  }}
                >
                  {t.QYXA(orderNewSimCardMsg)}
                </Link>
              </p>
            )}
            <DataLoader />
          </>
        </SelectOneDialog>
      )}
    </>
  );
};
